/* Modified by CheckoutWorks.dev */

/**
 * POD Size Chart Builder.
 *
 * The first part is pure logic (unit conversion, formatting, T-shirt outline
 * geometry, and generation of the merchant's Custom Liquid code) with no DOM
 * access, so the data build script and Node tests can import it.
 * The <cw-size-chart-builder> element at the end only registers in a browser.
 */

export const GENERATOR_VERSION = '1.0.0';
export const TOOL_URL = 'https://checkoutworks.dev/pages/shopify-size-chart-builder';
export const DEFAULT_INCH_COUNTRIES = ['US', 'CA', 'GB', 'IE', 'AU', 'NZ'];

export const COLUMN_INFO = {
  chest: { letter: 'A', label: 'Chest width', how: 'Laid flat, straight across the chest from side seam to side seam.' },
  length: { letter: 'B', label: 'Body length', how: 'From the highest point of the shoulder to the bottom hem.' },
  sleeve: { letter: 'C', label: 'Sleeve length', how: 'From the center back of the neck to the sleeve hem.' },
};

export const APPEARANCES = {
  inherit: null,
  light: { bg: '#ffffff', text: '#1a1a1a', head: '#f2f1ee', accent: '#1a1a1a', line: '#e3e1dc' },
  dark: { bg: '#1a1a1a', text: '#ffffff', head: '#262626', accent: '#34c779', line: '#3d3d3d' },
  warm: { bg: '#faf7f2', text: '#2e2a25', head: '#efe8dc', accent: '#7a5c3a', line: '#e3dac9' },
};

const CM_PER_INCH = 2.54;
const FRACTIONS = { 1: '⅛', 2: '¼', 3: '⅜', 4: '½', 5: '⅝', 6: '¾', 7: '⅞' };

/* ---------- Escaping ---------- */

export function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** HTML-escapes merchant-editable text and neutralizes Liquid delimiters. */
export function escapeText(value) {
  return escapeHtml(value).replace(/\{/g, '&#123;').replace(/\}/g, '&#125;');
}

/* ---------- Units and formatting ---------- */

export function roundTo(value, step) {
  return Math.round(value / step) * step;
}

export function convert(value, from, to) {
  if (from === to) return value;
  return from === 'in' ? value * CM_PER_INCH : value / CM_PER_INCH;
}

function trimNumber(value, decimals) {
  return String(Number(value.toFixed(decimals)));
}

/** @param {'fraction'|'decimal'} format */
export function formatInches(value, format = 'fraction') {
  if (format === 'decimal') return trimNumber(value, 3);
  const eighths = Math.round(value * 8);
  const whole = Math.floor(eighths / 8);
  const rest = eighths % 8;
  if (!rest) return String(whole);
  const fraction = FRACTIONS[rest];
  return whole ? `${whole}${fraction}` : fraction;
}

/** @param {'tenth'|'half'|'whole'} rounding */
export function formatCm(value, rounding = 'tenth') {
  if (rounding === 'whole') return String(Math.round(value));
  if (rounding === 'half') return trimNumber(roundTo(value, 0.5), 1);
  return roundTo(value, 0.1).toFixed(1);
}

/** Formats a source value in the requested display unit. */
export function formatValue(value, sourceUnit, targetUnit, options = {}) {
  const converted = convert(value, sourceUnit, targetUnit);
  return targetUnit === 'in'
    ? formatInches(converted, options.inchFormat)
    : formatCm(converted, options.cmRounding);
}

export function normalizeCountries(input) {
  const list = Array.isArray(input) ? input : String(input ?? '').split(/[\s,;]+/);
  const codes = list.map((code) => String(code).trim().toUpperCase()).filter((code) => /^[A-Z]{2}$/.test(code));
  return [...new Set(codes)];
}

/* ---------- Colors ---------- */

export function isHexColor(value) {
  return /^#[0-9a-f]{6}$/i.test(String(value ?? ''));
}

function luminance(hex) {
  const channels = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255);
  const [r, g, b] = channels.map((c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrastRatio(a, b) {
  const [light, dark] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (light + 0.05) / (dark + 0.05);
}

/** Returns the colors used by the chart, or null when it inherits the theme. */
export function resolveColors(state) {
  if (state.appearance === 'custom') return { ...APPEARANCES.light, ...pickHex(state.custom) };
  return APPEARANCES[state.appearance] ?? null;
}

function pickHex(colors = {}) {
  return Object.fromEntries(Object.entries(colors).filter(([, value]) => isHexColor(value)));
}

/** WCAG AA checks for body text on the chart background and table header. */
export function contrastChecks(colors) {
  if (!colors) return [];
  return [
    { id: 'text-bg', label: 'Text on background', ratio: contrastRatio(colors.text, colors.bg) },
    { id: 'text-head', label: 'Text on table header', ratio: contrastRatio(colors.text, colors.head) },
  ].map((check) => ({ ...check, pass: check.ratio >= 4.5 }));
}

/* ---------- State ---------- */

export function defaultState(blanks) {
  const first = blanks[0];
  return {
    selected: first ? [first.id] : [],
    perBlank: Object.fromEntries(blanks.map((blank) => [blank.id, { hiddenSizes: [], columns: blank.columns.map((c) => c.key) }])),
    inchFormat: 'fraction',
    cmRounding: 'tenth',
    countries: [...DEFAULT_INCH_COUNTRIES],
    display: 'popup',
    visibility: 'all',
    buttonLabel: 'Size Guide',
    heading: 'Size & Fit Guide',
    showModel: true,
    diagram: true,
    appearance: 'inherit',
    custom: { ...APPEARANCES.light },
  };
}

/** Merges stored state with defaults and drops anything invalid. */
export function sanitizeState(raw, blanks) {
  const base = defaultState(blanks);
  if (!raw || typeof raw !== 'object') return base;
  const ids = new Set(blanks.map((blank) => blank.id));
  const pick = (value, allowed, fallback) => (allowed.includes(value) ? value : fallback);
  const state = { ...base };

  const selected = Array.isArray(raw.selected) ? raw.selected.filter((id) => ids.has(id)) : [];
  state.selected = blanks.map((blank) => blank.id).filter((id) => selected.includes(id));
  if (!state.selected.length) state.selected = base.selected;

  for (const blank of blanks) {
    const stored = raw.perBlank?.[blank.id];
    if (!stored) continue;
    const sizes = Array.isArray(stored.hiddenSizes) ? stored.hiddenSizes.filter((s) => blank.sizes.includes(s)) : [];
    const keys = blank.columns.map((c) => c.key);
    const columns = Array.isArray(stored.columns) ? keys.filter((key) => stored.columns.includes(key)) : keys;
    state.perBlank[blank.id] = {
      hiddenSizes: sizes.length < blank.sizes.length ? sizes : [],
      columns: columns.length ? columns : keys,
    };
  }

  state.inchFormat = pick(raw.inchFormat, ['fraction', 'decimal'], base.inchFormat);
  state.cmRounding = pick(raw.cmRounding, ['tenth', 'half', 'whole'], base.cmRounding);
  state.display = pick(raw.display, ['popup', 'inline'], base.display);
  state.visibility = pick(raw.visibility, ['tagged', 'all'], base.visibility);
  state.appearance = pick(raw.appearance, Object.keys(APPEARANCES).concat('custom'), base.appearance);
  if (Array.isArray(raw.countries)) state.countries = normalizeCountries(raw.countries);
  if (typeof raw.buttonLabel === 'string') state.buttonLabel = raw.buttonLabel.slice(0, 40);
  if (typeof raw.heading === 'string') state.heading = raw.heading.slice(0, 60);
  if (typeof raw.showModel === 'boolean') state.showModel = raw.showModel;
  if (typeof raw.diagram === 'boolean') state.diagram = raw.diagram;
  state.custom = { ...base.custom, ...pickHex(raw.custom) };
  return state;
}

export function selectedBlanks(state, blanks) {
  return blanks.filter((blank) => state.selected.includes(blank.id));
}

/** True when the snippet renders for every product instead of matching tags. */
export function rendersEverywhere(state) {
  return state.selected.length === 1 && state.visibility === 'all';
}

/** Rows and columns shown for a blank after the merchant's choices. */
export function chartData(blank, state) {
  const config = state.perBlank?.[blank.id] ?? { hiddenSizes: [], columns: blank.columns.map((c) => c.key) };
  const rows = blank.sizes.map((size, index) => ({ size, index })).filter(({ size }) => !config.hiddenSizes.includes(size));
  const columns = blank.columns.filter((column) => config.columns.includes(column.key));
  return { rows, columns };
}

export function blankTitle(blank) {
  return `${blank.brand} ${blank.model}`;
}

/* ---------- T-shirt outline geometry ---------- */

/**
 * Builds a front-view T-shirt outline in inches, proportioned from real
 * measurements. y grows downward from the highest point of the shoulder.
 * Neck width, shoulder slope, and sleeve angle are fixed drawing
 * conventions; chest width, body length, and sleeve length drive the shape.
 */
export function teeGeometry({ chest, length, sleeve }) {
  const half = chest / 2;
  const neckHalf = 3.6;
  const shoulder = { x: half * 0.93, y: 1.4 };
  const armpit = { x: half, y: Math.max(7.5, chest * 0.43) };
  const sleeveTop = sleeve ? Math.max(4.5, sleeve - shoulder.x) : 7;
  const angle = (28 * Math.PI) / 180;
  const dir = { x: Math.cos(angle), y: Math.sin(angle) };
  const cuffTop = { x: shoulder.x + sleeveTop * dir.x, y: shoulder.y + sleeveTop * dir.y };
  const cuffBottom = { x: cuffTop.x - 6.2 * dir.y, y: cuffTop.y + 6.2 * dir.x };
  const hem = { x: half, y: length };
  const f = (n) => Number(n.toFixed(2));
  const right = [shoulder, cuffTop, cuffBottom, armpit, hem];
  const left = right.map((p) => ({ x: -p.x, y: p.y })).reverse();
  const path =
    `M${f(neckHalf)} 0 ` +
    right.map((p) => `L${f(p.x)} ${f(p.y)}`).join(' ') +
    ' ' +
    left.map((p) => `L${f(p.x)} ${f(p.y)}`).join(' ') +
    ` L${f(-neckHalf)} 0 C${f(-neckHalf * 0.55)} 3 ${f(neckHalf * 0.55)} 3 ${f(neckHalf)} 0 Z`;

  return {
    path,
    bounds: { minX: -cuffTop.x, maxX: cuffTop.x, minY: 0, maxY: Math.max(length, cuffBottom.y) },
    points: { neckHalf, shoulder, armpit, cuffTop, cuffBottom, hem },
    lines: {
      chest: { from: { x: -half, y: armpit.y + 1 }, to: { x: half, y: armpit.y + 1 } },
      length: { from: { x: neckHalf + 0.6, y: 0.2 }, to: { x: neckHalf + 0.6, y: length } },
      sleeve: {
        points: [
          { x: 0, y: 0.3 },
          { x: shoulder.x, y: shoulder.y },
          { x: (cuffTop.x + cuffBottom.x) / 2, y: (cuffTop.y + cuffBottom.y) / 2 },
        ],
      },
    },
  };
}

/** Size-M (or middle size) measurements used to proportion the outline. */
export function referenceMeasurements(blank) {
  let index = blank.sizes.indexOf('M');
  if (index < 0) index = Math.floor(blank.sizes.length / 2);
  const value = (key) => {
    const column = blank.columns.find((c) => c.key === key);
    return column ? convert(column.values[index], blank.unit, 'in') : undefined;
  };
  return { size: blank.sizes[index], chest: value('chest'), length: value('length'), sleeve: value('sleeve') };
}

/**
 * Renders the outline as an SVG string.
 * @param {object} blank
 * @param {{ columns?: string[], labels?: boolean, values?: boolean, title?: string, className?: string }} options
 */
export function teeSvg(blank, options = {}) {
  const ref = referenceMeasurements(blank);
  const geo = teeGeometry(ref);
  const pad = options.values ? 3.2 : 1.6;
  const { minX, maxX, maxY } = geo.bounds;
  const viewBox = [minX - pad, -pad, maxX - minX + pad * 2, maxY + pad * 2].map((n) => Number(n.toFixed(2))).join(' ');
  const columns = options.columns ?? blank.columns.map((c) => c.key);
  const f = (n) => Number(n.toFixed(2));
  const marks = [];

  for (const key of columns) {
    const info = COLUMN_INFO[key];
    const line = geo.lines[key];
    if (!info || !line) continue;
    const text = options.values ? `${formatInches(ref[key])} in` : info.letter;
    if (key === 'sleeve') {
      const pts = line.points.map((p) => `${f(p.x)},${f(p.y)}`).join(' ');
      const end = line.points[2];
      marks.push(`<polyline class="cw-dim" points="${pts}"/>`);
      marks.push(`<text class="cw-dim-label" x="${f(end.x + 1.6)}" y="${f(end.y + 0.4)}">${text}</text>`);
    } else {
      const { from, to } = line;
      const tick = key === 'chest' ? `M${f(from.x)} ${f(from.y - 0.7)}V${f(from.y + 0.7)}M${f(to.x)} ${f(to.y - 0.7)}V${f(to.y + 0.7)}` : `M${f(from.x - 0.7)} ${f(from.y)}H${f(from.x + 0.7)}M${f(to.x - 0.7)} ${f(to.y)}H${f(to.x + 0.7)}`;
      marks.push(`<path class="cw-dim" d="M${f(from.x)} ${f(from.y)}L${f(to.x)} ${f(to.y)}${tick}"/>`);
      const label = key === 'chest' ? { x: 0, y: from.y - 1 } : { x: to.x + 1.2, y: (from.y + to.y) / 2 };
      const anchor = key === 'chest' ? 'middle' : 'start';
      marks.push(`<text class="cw-dim-label" x="${f(label.x)}" y="${f(label.y)}" text-anchor="${anchor}">${text}</text>`);
    }
  }

  const title = options.title ? `<title>${escapeHtml(options.title)}</title>` : '';
  const role = options.title ? 'role="img"' : 'aria-hidden="true" focusable="false"';
  return `<svg class="${options.className ?? ''}" viewBox="${viewBox}" ${role} xmlns="http://www.w3.org/2000/svg">${title}<path class="cw-outline" d="${geo.path}"/>${options.labels === false ? '' : marks.join('')}</svg>`;
}

/* ---------- Merchant snippet ---------- */

function unitSpans(inches, cm) {
  return `<span class="cw-sc-in">${inches}</span><span class="cw-sc-cm">${cm}</span>`;
}

/**
 * Chart markup for one blank.
 * @param {'liquid'|boolean} inches Liquid expression for the default unit, or a fixed boolean for previews.
 */
export function chartHtml(blank, state, inches) {
  const { rows, columns } = chartData(blank, state);
  const fmt = { inchFormat: state.inchFormat, cmRounding: state.cmRounding };
  const checked = inches === 'liquid' ? '{% if cw_sc_inches %} checked{% endif %}' : inches ? ' checked' : '';
  const heading = escapeText(state.heading || 'Size Guide');
  const meta = state.showModel ? `<p class="cw-sc-meta">${escapeHtml(blankTitle(blank))} · ${escapeHtml(blank.fit)}</p>` : '';

  const head = columns
    .map((c) => `<th scope="col"><span class="cw-sc-letter">${COLUMN_INFO[c.key].letter}</span> ${COLUMN_INFO[c.key].label} ${unitSpans('(in)', '(cm)')}</th>`)
    .join('');
  const body = rows
    .map(({ size, index }) => {
      const cells = columns
        .map((c) => `<td>${unitSpans(formatValue(c.values[index], blank.unit, 'in', fmt), formatValue(c.values[index], blank.unit, 'cm', fmt))}</td>`)
        .join('');
      return `<tr><th scope="row">${escapeHtml(size)}</th>${cells}</tr>`;
    })
    .join('');

  const how = columns
    .map((c) => `<div><dt><span class="cw-sc-letter">${COLUMN_INFO[c.key].letter}</span> ${COLUMN_INFO[c.key].label}</dt><dd>${COLUMN_INFO[c.key].how}</dd></div>`)
    .join('');
  const diagram = state.diagram
    ? `<div class="cw-sc-guide">${teeSvg(blank, { columns: columns.map((c) => c.key), className: 'cw-sc-diagram' })}<dl class="cw-sc-how">${how}</dl></div>`
    : '';

  return (
    `<div class="cw-sc-chart">` +
    `<div class="cw-sc-head"><div><h2 class="cw-sc-title">${heading}</h2>${meta}</div>` +
    `<label class="cw-sc-switch"><span aria-hidden="true">CM</span><input type="checkbox" role="switch" aria-label="Show measurements in inches"${checked}><span class="cw-sc-track" aria-hidden="true"></span><span aria-hidden="true">IN</span></label></div>` +
    diagram +
    `<div class="cw-sc-scroll" role="region" aria-label="${escapeHtml(blankTitle(blank))} measurements" tabindex="0">` +
    `<table class="cw-sc-table"><thead><tr><th scope="col">Size</th>${head}</tr></thead><tbody>${body}</tbody></table></div>` +
    `<p class="cw-sc-note">Garment measurements in ${unitSpans('inches', 'centimeters')}. Measurements can vary slightly between garments.</p>` +
    `</div>`
  );
}

const RULER_ICON =
  '<svg class="cw-sc-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><rect x="2.5" y="8" width="19" height="8" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.25"/><path d="M6.5 8v3M10 8v2M13.5 8v3M17 8v2" stroke="currentColor" stroke-width="1.25" stroke-linecap="round"/></svg>';
const CLOSE_ICON =
  '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"/></svg>';

/** Wraps chart markup for the chosen display mode. */
export function wrapperHtml(state, inner) {
  if (state.display === 'inline') return `<div class="cw-sc cw-sc--inline">${inner}</div>`;
  const label = escapeText(state.buttonLabel || 'Size Guide');
  const heading = escapeText(state.heading || 'Size Guide');
  return (
    `<div class="cw-sc" data-cw-sc>` +
    `<button type="button" class="cw-sc-open" data-cw-sc-open aria-haspopup="dialog">${RULER_ICON}<span>${label}</span></button>` +
    `<dialog class="cw-sc-dialog" aria-label="${heading}"><div class="cw-sc-panel">` +
    `<button type="button" class="cw-sc-close" data-cw-sc-close aria-label="Close">${CLOSE_ICON}</button>${inner}</div></dialog></div>`
  );
}

export function snippetCss(state) {
  const colors = resolveColors(state);
  const vars = colors
    ? `--cw-sc-bg: ${colors.bg}; --cw-sc-text: ${colors.text}; --cw-sc-head: ${colors.head}; --cw-sc-accent: ${colors.accent}; --cw-sc-line: ${colors.line};`
    : '--cw-sc-bg: transparent; --cw-sc-text: currentColor; --cw-sc-head: color-mix(in srgb, currentColor 6%, transparent); --cw-sc-accent: currentColor; --cw-sc-line: color-mix(in srgb, currentColor 16%, transparent);';
  const surface = colors ? 'background: var(--cw-sc-bg); color: var(--cw-sc-text);' : '';

  return `
.cw-sc { ${vars} }
.cw-sc, .cw-sc * { box-sizing: border-box; }
.cw-sc .cw-sc-open { display: inline-flex; align-items: center; gap: 0.375em; min-height: 0; margin: 0; padding: 0.25em 0; border: 0; border-radius: 0; background: none; box-shadow: none; color: inherit; font: inherit; font-size: 0.875em; line-height: 1.4; letter-spacing: inherit; text-transform: none; cursor: pointer; }
.cw-sc .cw-sc-open span { text-decoration: underline; text-decoration-thickness: 1px; text-underline-offset: 0.2em; }
.cw-sc .cw-sc-open:hover span { text-decoration-thickness: 2px; }
.cw-sc .cw-sc-icon { width: 1.125em; height: 1.125em; flex: none; opacity: 0.8; }
.cw-sc .cw-sc-open:focus-visible, .cw-sc .cw-sc-close:focus-visible, .cw-sc .cw-sc-scroll:focus-visible { outline: 2px solid currentColor; outline-offset: 2px; }
.cw-sc .cw-sc-dialog { width: min(40rem, calc(100% - 2rem)); max-width: none; max-height: min(85vh, 48rem); margin: auto; padding: 0; border: 1px solid var(--cw-sc-line); border-radius: 0.75rem; background: var(--cw-sc-page-bg, var(--cw-sc-bg)); color: inherit; font: inherit; }
.cw-sc .cw-sc-dialog::backdrop { background: rgb(0 0 0 / 0.5); }
html:has(.cw-sc-dialog[open]) { overflow: hidden; }
.cw-sc .cw-sc-panel { position: relative; padding: 1.5rem; ${surface} }
.cw-sc--inline .cw-sc-chart { ${colors ? `padding: 1.25rem; border-radius: 0.5rem; ${surface}` : ''} }
.cw-sc .cw-sc-close { position: absolute; top: 0.75rem; right: 0.75rem; display: grid; place-items: center; width: 2.5rem; height: 2.5rem; margin: 0; padding: 0; border: 0; border-radius: 50%; background: none; color: inherit; cursor: pointer; }
.cw-sc .cw-sc-close svg { width: 1.25rem; height: 1.25rem; }
.cw-sc .cw-sc-head { display: flex; flex-wrap: wrap; align-items: flex-start; justify-content: space-between; gap: 0.75rem 1.5rem; margin-bottom: 1rem; }
.cw-sc-dialog .cw-sc-head { padding-right: 2.5rem; }
.cw-sc .cw-sc-title { margin: 0; font: inherit; font-size: 1.25em; font-weight: 600; line-height: 1.3; color: inherit; }
.cw-sc .cw-sc-meta { margin: 0.25rem 0 0; font-size: 0.875em; opacity: 0.75; }
.cw-sc .cw-sc-switch { position: relative; display: inline-flex; align-items: center; gap: 0.5rem; margin: 0; font-size: 0.8125em; font-weight: 600; letter-spacing: 0.05em; cursor: pointer; user-select: none; }
.cw-sc .cw-sc-switch input { position: absolute; width: 1px; height: 1px; margin: 0; padding: 0; border: 0; opacity: 0; appearance: none; pointer-events: none; }
.cw-sc .cw-sc-track { position: relative; display: inline-block; flex: none; width: 2.75rem; height: 1.5rem; border: 1px solid var(--cw-sc-line); border-radius: 1rem; background: var(--cw-sc-head); }
.cw-sc .cw-sc-track::after { content: ""; position: absolute; top: 0.1875rem; left: 0.1875rem; width: 1rem; height: 1rem; border-radius: 50%; background: var(--cw-sc-accent); transition: transform 0.2s ease; }
.cw-sc .cw-sc-switch input:checked + .cw-sc-track::after { transform: translateX(1.25rem); }
.cw-sc .cw-sc-switch input:focus-visible + .cw-sc-track { outline: 2px solid currentColor; outline-offset: 2px; }
.cw-sc-chart:not(:has(.cw-sc-switch input:checked)) .cw-sc-in, .cw-sc-chart:has(.cw-sc-switch input:checked) .cw-sc-cm { display: none; }
.cw-sc .cw-sc-guide { display: flex; flex-wrap: wrap; align-items: center; gap: 1rem 1.25rem; margin-bottom: 1rem; }
.cw-sc .cw-sc-diagram { width: 7.5rem; height: auto; flex: none; overflow: visible; }
.cw-sc .cw-sc-diagram .cw-outline { fill: var(--cw-sc-head); stroke: currentColor; stroke-width: 0.25; stroke-linejoin: round; }
.cw-sc .cw-sc-diagram .cw-dim { fill: none; stroke: var(--cw-sc-accent); stroke-width: 0.3; stroke-dasharray: 0.8 0.5; }
.cw-sc .cw-sc-diagram .cw-dim-label { fill: currentColor; font-size: 2.2px; font-weight: 700; }
.cw-sc .cw-sc-how { display: grid; flex: 1 1 14rem; gap: 0.5rem; min-width: 0; margin: 0; font-size: 0.8125em; line-height: 1.4; }
.cw-sc .cw-sc-how dt { font-weight: 600; }
.cw-sc .cw-sc-how dd { margin: 0; opacity: 0.8; }
.cw-sc .cw-sc-letter { display: inline-grid; place-items: center; min-width: 1.35em; height: 1.35em; margin-right: 0.15em; border: 1px solid currentColor; border-radius: 50%; font-size: 0.75em; font-weight: 700; line-height: 1; vertical-align: 0.1em; }
.cw-sc .cw-sc-scroll { overflow-x: auto; border: 1px solid var(--cw-sc-line); border-radius: 0.5rem; }
.cw-sc .cw-sc-table { width: 100%; margin: 0; border: 0; border-collapse: collapse; font-size: 0.875em; font-variant-numeric: tabular-nums; }
.cw-sc .cw-sc-table th, .cw-sc .cw-sc-table td { padding: 0.625rem 0.75rem; border: 0; border-bottom: 1px solid var(--cw-sc-line); text-align: left; white-space: nowrap; background: none; color: inherit; }
.cw-sc .cw-sc-table thead th { background: var(--cw-sc-head); font-weight: 600; white-space: normal; vertical-align: bottom; }
.cw-sc .cw-sc-table tbody tr:last-child > * { border-bottom: 0; }
.cw-sc .cw-sc-table tbody th { font-weight: 600; }
.cw-sc .cw-sc-note { margin: 0.75rem 0 0; font-size: 0.8125em; opacity: 0.75; }
@media (max-width: 480px) { .cw-sc .cw-sc-panel { padding: 1.25rem 1rem; } .cw-sc .cw-sc-guide { align-items: flex-start; } .cw-sc .cw-sc-diagram { width: 5.5rem; } .cw-sc .cw-sc-table th, .cw-sc .cw-sc-table td { padding: 0.5rem; } }
@media (prefers-reduced-motion: reduce) { .cw-sc .cw-sc-track::after { transition: none; } }
`.trim();
}

/** Minimal delegated handler: opens/closes the dialog and matches its background to the page. */
export const SNIPPET_SCRIPT = `(() => {
  if (window.cwSizeChart) return;
  window.cwSizeChart = true;
  const pageBackground = (element) => {
    for (let node = element; node; node = node.parentElement) {
      const color = getComputedStyle(node).backgroundColor;
      if (color && !/^(transparent|rgba\\(.*,\\s*0\\))$/.test(color)) return color;
    }
    return '#fff';
  };
  document.addEventListener('click', (event) => {
    const open = event.target.closest('[data-cw-sc-open]');
    if (open) {
      const dialog = open.closest('[data-cw-sc]').querySelector('.cw-sc-dialog');
      dialog.style.setProperty('--cw-sc-page-bg', pageBackground(open));
      dialog.showModal();
      return;
    }
    if (event.target.closest('[data-cw-sc-close]') || event.target.matches('.cw-sc-dialog')) {
      event.target.closest('dialog').close();
    }
  });
})();`;

/**
 * Generates the complete code for a Custom Liquid block.
 * @param {object} state
 * @param {object[]} blanks
 * @param {{ date?: string }} meta
 */
export function generateSnippet(state, blanks, meta = {}) {
  const chosen = selectedBlanks(state, blanks);
  if (!chosen.length) return '';
  const everywhere = rendersEverywhere(state);
  const countries = normalizeCountries(state.countries);
  const date = meta.date ?? new Date().toISOString().slice(0, 10);

  const header = [
    '{% comment %}',
    `  Size chart: ${chosen.map(blankTitle).join(', ')} · v${GENERATOR_VERSION} · ${date}`,
    ...(everywhere ? [] : [`  Product tags: ${chosen.map((b) => b.tag).join(', ')}`]),
    `  Built with ${TOOL_URL}. To update, generate again and replace this code.`,
    '{% endcomment %}',
  ].join('\n');

  const liquid = ['{%- liquid'];
  if (everywhere) {
    liquid.push(`  assign cw_sc_chart = '${chosen[0].id}'`);
  } else {
    liquid.push("  assign cw_sc_chart = ''", "  assign cw_sc_tags = product.tags | join: '|' | downcase | split: '|'");
    chosen.forEach((b, i) => {
      liquid.push(`  ${i === 0 ? 'if' : 'elsif'} cw_sc_tags contains '${b.tag}'`, `    assign cw_sc_chart = '${b.id}'`);
    });
    liquid.push('  endif');
  }
  liquid.push(
    `  assign cw_sc_inch_countries = '${countries.join(',')}' | split: ','`,
    '  assign cw_sc_inches = false',
    '  if cw_sc_inch_countries contains localization.country.iso_code',
    '    assign cw_sc_inches = true',
    '  endif',
    '-%}'
  );

  const charts =
    chosen.length === 1
      ? chartHtml(chosen[0], state, 'liquid')
      : `{%- case cw_sc_chart -%}\n${chosen.map((b) => `{%- when '${b.id}' -%}\n${chartHtml(b, state, 'liquid')}`).join('\n')}\n{%- endcase -%}`;

  const script = state.display === 'popup' ? `\n<script>\n${SNIPPET_SCRIPT}\n</script>` : '';
  const markup = formatMarkup(wrapperHtml(state, `\n${charts}\n`));

  return `${header}\n${liquid.join('\n')}\n{%- if cw_sc_chart != blank -%}\n${markup}\n{%- style -%}\n${snippetCss(state)}\n{%- endstyle -%}${script}\n{%- endif -%}\n`;
}

/** Breaks generated markup onto separate lines at block boundaries so the file is readable. */
export function formatMarkup(html) {
  return html
    .replace(/(<\/(?:div|dl|table|thead|tbody|tr|p|h2|button|label|dialog|svg)>)(?=<)/g, '$1\n')
    .replace(/(<(?:div|dl|table|thead|tbody|dialog)\b[^>]*>)(?=<)/g, '$1\n')
    .replace(/\n{2,}/g, '\n');
}

/** Static preview of what shoppers see (no Liquid). */
export function previewHtml(state, blank, inches) {
  return wrapperHtml(state, chartHtml(blank, state, inches));
}

export function altText(blank, state) {
  const { rows, columns } = chartData(blank, state);
  const measures = columns.map((c) => COLUMN_INFO[c.key].label.toLowerCase());
  const list = measures.length > 1 ? `${measures.slice(0, -1).join(', ')} and ${measures.at(-1)}` : measures[0];
  const range = rows.length ? `, sizes ${rows[0].size} to ${rows.at(-1).size}` : '';
  const model = state.showModel ? ` for ${blankTitle(blank)} ${blank.name}` : ' for a unisex T-shirt';
  return `Size chart${model}: ${list} in inches and centimeters${range}.`;
}

/* ---------- Builder UI (browser only) ---------- */

const CORE = {
  defaultState,
  escapeHtml,
  generateSnippet,
  normalizeCountries,
  previewHtml,
  snippetCss,
  teeSvg,
};

if (typeof window !== 'undefined' && window.customElements && !customElements.get('cw-size-chart-builder')) {

  const PREVIEW_CSS = `
  :host { display: block; }
  .cw-sc-table td > span { animation: cw-scb-fade 0.25s ease both; }
  @keyframes cw-scb-fade { from { opacity: 0; } }
  @media (prefers-reduced-motion: reduce) { .cw-sc-table td > span { animation: none; } }
  `;

  function highlight(code, escapeHtml) {
    return escapeHtml(code)
      .replace(/(&lt;\/?)([a-z][a-z0-9-]*)/g, '$1<span class="t-tag">$2</span>')
      .replace(/(\{% comment %\}[\s\S]*?\{% endcomment %\})|(\{%-?[\s\S]*?-?%\}|\{\{[\s\S]*?\}\})/g, (match, comment) =>
        comment ? `<span class="t-comment">${match}</span>` : `<span class="t-liquid">${match}</span>`
      );
  }

  async function copyText(text) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      const area = document.createElement('textarea');
      area.value = text;
      area.setAttribute('readonly', '');
      area.style.cssText = 'position:fixed;opacity:0';
      document.body.append(area);
      area.select();
      const ok = document.execCommand('copy');
      area.remove();
      return ok;
    }
  }

  /**
   * The builder has one choice: the blank. Every other option uses the core
   * module's defaults (theme colors, popup, country-based units, diagram).
   */
  class SizeChartBuilder extends HTMLElement {
    connectedCallback() {
      if (this.core) return;
      const data = this.querySelector('[data-cw-scb-data]');
      if (!data) return;

      this.blanks = JSON.parse(data.textContent);
      this.core = CORE;
      this.state = this.core.defaultState(this.blanks);
      // Start from the blank checked in the server-rendered HTML (the first one).
      const checked = this.querySelector('[name="cw-scb-blank"]:checked');
      if (checked) this.state.selected = [checked.value];
      this.addEventListener('change', (event) => {
        if (event.target.name !== 'cw-scb-blank') return;
        this.state.selected = [event.target.value];
        this.render();
      });
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-action="copy-code"]');
        if (button) this.copy(button);
      });

      this.render();
      this.classList.add('is-ready');
    }

    region(name) {
      return this.querySelector(`[data-slot="${name}"]`);
    }

    render() {
      const { core, state } = this;
      const blank = this.blanks.find((b) => b.id === state.selected[0]);
      if (!blank) return;

      this.code = core.generateSnippet(state, this.blanks);
      this.region('code').innerHTML = highlight(this.code, core.escapeHtml);
      this.region('preview-media').innerHTML = core.teeSvg(blank, { labels: false });
      this.region('preview-model').textContent = `${blank.brand} ${blank.model}`;

      const host = this.region('preview');
      if (!this.previewRoot) {
        this.previewRoot = host.attachShadow({ mode: 'open' });
        this.previewRoot.addEventListener('click', (event) => {
          const dialog = this.previewRoot.querySelector('.cw-sc-dialog');
          if (!dialog) return;
          if (event.target.closest('[data-cw-sc-open]')) {
            dialog.style.setProperty('--cw-sc-page-bg', getComputedStyle(host.closest('.cw-scb-page')).backgroundColor);
            dialog.showModal();
          } else if (event.target.closest('[data-cw-sc-close]') || event.target === dialog) {
            dialog.close();
          }
        });
      }
      // Preview in the unit the visitor's storefront country gets, as their own shoppers would.
      const country = this.dataset.country;
      const inches = country ? core.normalizeCountries(state.countries).includes(country) : true;
      this.previewRoot.innerHTML = `<style>${core.snippetCss(state)}${PREVIEW_CSS}</style>${core.previewHtml(state, blank, inches)}`;
      if (country) {
        const name = this.dataset.countryName || country;
        this.region('preview-note').textContent = `${name} sees ${inches ? 'inches' : 'centimeters'} first.`;
      }
    }

    async copy(button) {
      const ok = await copyText(this.code);
      const status = this.region('status');
      status.textContent = ok ? 'Code copied.' : 'Copy failed. Select the code and copy it manually.';
      if (!ok) return;
      const label = button.textContent;
      button.classList.add('is-done');
      button.textContent = 'Copied';
      clearTimeout(button.cwTimer);
      button.cwTimer = setTimeout(() => {
        button.classList.remove('is-done');
        button.textContent = label;
      }, 1600);
    }
  }

  customElements.define('cw-size-chart-builder', SizeChartBuilder);
}
